import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Euro, AlertTriangle, CheckCircle, XCircle, Edit, Save, RefreshCw,
  Calendar, FileText, Users, Clock, ChevronDown, ChevronUp, Bell,
  Download, Upload, Plus, Trash2, ExternalLink, AlertCircle, Info,
  Building2, Landmark, Home, Building
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping des catégories de bénéficiaires
const BENEFICIARY_CATEGORIES = {
  particulier: {
    label: "Particuliers",
    icon: Home,
    color: "bg-blue-100 text-blue-700",
    description: "Propriétaires occupants, bailleurs, locataires"
  },
  entreprise: {
    label: "Entreprises",
    icon: Building2,
    color: "bg-purple-100 text-purple-700",
    description: "TPE, PME, ETI, grandes entreprises"
  },
  collectivite: {
    label: "Collectivités",
    icon: Landmark,
    color: "bg-amber-100 text-amber-700",
    description: "Communes, EPCI, départements, régions"
  },
  bailleur_social: {
    label: "Bailleurs Sociaux",
    icon: Building,
    color: "bg-emerald-100 text-emerald-700",
    description: "Offices HLM, ESH, SEM, coopératives"
  }
};

export default function AdminProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedProgram, setEditedProgram] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [alertsCount, setAlertsCount] = useState({ unreviewed: 0, total: 0 });
  const [activeMainTab, setActiveMainTab] = useState("programs");
  const [activeBeneficiaryTab, setActiveBeneficiaryTab] = useState("particulier");
  
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchPrograms();
    fetchAlerts();
  }, []);

  // Filtrer les programmes par catégorie de bénéficiaire
  const programsByCategory = useMemo(() => {
    const categorized = {
      particulier: [],
      entreprise: [],
      collectivite: [],
      bailleur_social: []
    };
    
    programs.forEach(program => {
      const types = program.beneficiary_types || [];
      if (types.length === 0) {
        // Si pas de type défini, mettre dans particuliers par défaut
        categorized.particulier.push(program);
      } else {
        types.forEach(type => {
          if (categorized[type]) {
            categorized[type].push(program);
          }
        });
      }
    });
    
    return categorized;
  }, [programs]);

  // Compter les programmes par catégorie
  const programCounts = useMemo(() => ({
    particulier: programsByCategory.particulier.length,
    entreprise: programsByCategory.entreprise.length,
    collectivite: programsByCategory.collectivite.length,
    bailleur_social: programsByCategory.bailleur_social.length
  }), [programsByCategory]);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/admin/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(response.data.programs || []);
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Erreur lors du chargement des programmes");
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/admin/rule-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
      setAlertsCount({
        unreviewed: response.data.unreviewed_count || 0,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const handleToggleProgram = async (programId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/programs/${programId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(programs.map(p => 
        p.program_id === programId ? { ...p, is_active: !currentStatus } : p
      ));
      toast.success(`Programme ${currentStatus ? 'désactivé' : 'activé'}`);
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const handleReviewAlert = async (alertId, action) => {
    try {
      await axios.put(`${API}/admin/rule-alerts/${alertId}/review`, 
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAlerts();
      toast.success(`Alerte ${action === 'approve' ? 'validée' : 'rejetée'}`);
    } catch (error) {
      toast.error("Erreur lors du traitement de l'alerte");
    }
  };

  const getStatusBadge = (program) => {
    const validTo = program.metadata?.valid_to;
    if (validTo) {
      const expiryDate = new Date(validTo);
      const now = new Date();
      if (expiryDate < now) {
        return <Badge variant="destructive" className="text-xs">Expiré</Badge>;
      }
    }
    return program.is_active ? 
      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Actif</Badge> : 
      <Badge variant="secondary" className="text-xs">Inactif</Badge>;
  };

  const getFundingTypeBadge = (type) => {
    switch(type) {
      case "subvention":
        return <Badge variant="outline" className="text-xs bg-green-50 border-green-200">Subvention</Badge>;
      case "pret":
        return <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200">Prêt</Badge>;
      default:
        return <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200">Avantage fiscal</Badge>;
    }
  };

  const renderProgramCard = (program) => {
    const isExpanded = expandedProgram === program.program_id;
    
    return (
      <div 
        key={program.program_id}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
      >
        {/* Header du programme */}
        <div 
          className="p-4 cursor-pointer"
          onClick={() => setExpandedProgram(isExpanded ? null : program.program_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                program.is_active ? 'bg-emerald-100' : 'bg-slate-100'
              }`}>
                <Euro className={`h-6 w-6 ${program.is_active ? 'text-emerald-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-[#1D1D1D]">{program.program_name}</h3>
                  {getStatusBadge(program)}
                  {getFundingTypeBadge(program.funding_type)}
                </div>
                <p className="text-sm text-slate-500">{program.payer} • {program.program_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-xs text-slate-500">Validité</p>
                <p className="text-sm font-medium">
                  {program.metadata?.valid_from || "—"} → {program.metadata?.valid_to || "∞"}
                </p>
              </div>
              <Switch
                checked={program.is_active}
                onCheckedChange={() => handleToggleProgram(program.program_id, program.is_active)}
                onClick={(e) => e.stopPropagation()}
              />
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSelectedProgram(program); }}>
                <Edit className="h-4 w-4" />
              </Button>
              {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
            </div>
          </div>
        </div>

        {/* Contenu étendu */}
        {isExpanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Conditions */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Conditions d'éligibilité
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {(program.global_conditions || []).slice(0, 5).map((condition, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>{condition}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Plafonds */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                  <Euro className="h-4 w-4 text-blue-500" />
                  Plafonds par catégorie
                </h4>
                {program.global_ceiling && typeof program.global_ceiling === 'object' ? (
                  <div className="space-y-1">
                    {Object.entries(program.global_ceiling).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                        <span className="font-medium">{typeof value === 'number' ? `${value.toLocaleString()} €` : value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Non défini</p>
                )}
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-amber-500" />
                  Documents requis
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {(program.required_documents || []).slice(0, 5).map((doc, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span>{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Source */}
            {program.metadata?.source_url && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <a 
                  href={program.metadata.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  Source officielle
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#86BC29]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-programs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1D1D1D]">Gestion des Primes</h1>
          <p className="text-slate-500">Configurez les règles et conditions des aides à la rénovation</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => { fetchPrograms(); fetchAlerts(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Alerte si alertes en attente */}
      {alertsCount.unreviewed > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {alertsCount.unreviewed} alerte{alertsCount.unreviewed > 1 ? 's' : ''} en attente de validation
                </p>
                <p className="text-sm text-amber-600">
                  Des changements de règles ont été détectés par le système de veille
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setActiveMainTab("alerts")}
            >
              Voir les alertes
            </Button>
          </div>
        </div>
      )}

      {/* Onglets principaux: Programmes / Alertes / Historique */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="programs" className="data-[state=active]:bg-white">
            <Euro className="h-4 w-4 mr-2" />
            Programmes ({programs.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-white">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Alertes Veille
            {alertsCount.unreviewed > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {alertsCount.unreviewed}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <Clock className="h-4 w-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Tab: Programmes avec sous-onglets par bénéficiaire */}
        <TabsContent value="programs" className="space-y-4">
          {/* Sous-onglets par catégorie de bénéficiaire */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <Tabs value={activeBeneficiaryTab} onValueChange={setActiveBeneficiaryTab}>
              <TabsList className="grid grid-cols-2 lg:grid-cols-4 gap-2 bg-transparent h-auto p-0">
                {Object.entries(BENEFICIARY_CATEGORIES).map(([key, cat]) => {
                  const Icon = cat.icon;
                  const count = programCounts[key];
                  return (
                    <TabsTrigger 
                      key={key} 
                      value={key}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                        data-[state=active]:border-[#86BC29] data-[state=active]:bg-[#86BC29]/5
                        data-[state=inactive]:border-slate-200 data-[state=inactive]:bg-slate-50
                        hover:border-[#86BC29]/50`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-sm">{cat.label}</p>
                        <p className="text-xs text-slate-500">{count} aide{count > 1 ? 's' : ''}</p>
                      </div>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Contenu de chaque catégorie */}
              {Object.entries(BENEFICIARY_CATEGORIES).map(([key, cat]) => (
                <TabsContent key={key} value={key} className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.color}`}>
                      <cat.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{cat.label}</h3>
                      <p className="text-sm text-slate-500">{cat.description}</p>
                    </div>
                  </div>

                  {programsByCategory[key].length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun programme pour cette catégorie</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {programsByCategory[key].map(program => renderProgramCard(program))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </TabsContent>

        {/* Tab: Alertes */}
        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white rounded-xl border">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
              <p className="font-medium">Aucune alerte en attente</p>
              <p className="text-sm">Le système de veille n'a détecté aucun changement récent</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div 
                  key={alert._id || alert.id}
                  className={`bg-white rounded-xl border p-4 ${
                    alert.severity === 'critical' ? 'border-red-200 bg-red-50/50' :
                    alert.severity === 'high' ? 'border-orange-200 bg-orange-50/50' :
                    'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        alert.severity === 'critical' ? 'bg-red-100' :
                        alert.severity === 'high' ? 'bg-orange-100' :
                        'bg-amber-100'
                      }`}>
                        <AlertCircle className={`h-5 w-5 ${
                          alert.severity === 'critical' ? 'text-red-600' :
                          alert.severity === 'high' ? 'text-orange-600' :
                          'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{alert.title || alert.source_name}</h4>
                          <Badge variant={
                            alert.severity === 'critical' ? 'destructive' :
                            alert.severity === 'high' ? 'warning' : 'secondary'
                          } className="text-xs capitalize">
                            {alert.severity || 'medium'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{alert.description || alert.change_summary}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          Détecté le {new Date(alert.detected_at || alert.created_at).toLocaleDateString('fr-FR')}
                          {alert.source_name && ` • Source: ${alert.source_name}`}
                        </p>
                      </div>
                    </div>
                    {!alert.reviewed && (
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleReviewAlert(alert._id || alert.id, 'approve')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Valider
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => handleReviewAlert(alert._id || alert.id, 'reject')}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Historique */}
        <TabsContent value="history" className="space-y-4">
          <div className="text-center py-12 text-slate-500 bg-white rounded-xl border">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">Historique des modifications</p>
            <p className="text-sm">Les modifications apportées aux règles seront affichées ici</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal d'édition */}
      <Dialog open={!!selectedProgram} onOpenChange={() => setSelectedProgram(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier {selectedProgram?.program_name}</DialogTitle>
          </DialogHeader>
          {selectedProgram && (
            <div className="space-y-4">
              <div>
                <Label>Nom du programme</Label>
                <Input value={selectedProgram.program_name} readOnly className="bg-slate-50" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={selectedProgram.description || ""} 
                  placeholder="Description du programme..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date de début</Label>
                  <Input 
                    type="date" 
                    value={selectedProgram.metadata?.valid_from || ""} 
                  />
                </div>
                <div>
                  <Label>Date de fin</Label>
                  <Input 
                    type="date" 
                    value={selectedProgram.metadata?.valid_to || ""} 
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSelectedProgram(null)}>
                  Annuler
                </Button>
                <Button className="bg-[#86BC29] hover:bg-[#86BC29]/90">
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
